import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ShieldCheck, Smartphone } from 'lucide-react';
import { resolveEntityFromHostname, tenantConfig } from '@/config/tenant';

export default function LoginPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const location = useLocation();
  const { login, validateMfa, getMfaQrCode, isLoading, mfaState } = useAuth();
  // Resolve the tenant from the hostname synchronously so the FIRST paint is
  // already branded — avoids the "ActiveID → tenant name" flash and the
  // Organization-field flash. The fetch below reverts it on an unknown subdomain.
  const initialSub = resolveEntityFromHostname();
  const initialName = initialSub
    ? initialSub.charAt(0).toUpperCase() + initialSub.slice(1).toLowerCase()
    : null;
  const [entity, setEntity] = useState(initialSub ?? import.meta.env.VITE_TENANT_CODE ?? '');
  const [userName, setUserName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [mfaError, setMfaError] = useState<string | null>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [isLoadingQr, setIsLoadingQr] = useState(false);
  // SaaS subdomain branding. brandingEntity != null means the hostname resolved
  // to a tenant — we auto-fill the entity and hide the Organization field. Seeded
  // optimistically from the subdomain so the first paint is branded; the fetch
  // below confirms the real display name + logo (or reverts on an unknown tenant).
  const [brandingEntity, setBrandingEntity] = useState<string | null>(initialSub);
  const [brandingName, setBrandingName] = useState<string | null>(initialName);
  const [brandingLogoUrl, setBrandingLogoUrl] = useState<string | null>(null);
  const [brandingHideTitle, setBrandingHideTitle] = useState(false);
  // For a subdomain tenant the title waits until branding resolves, so it never
  // flashes the default and never shows-then-hides for a logo-only tenant.
  const [brandingReady, setBrandingReady] = useState<boolean>(!initialSub);

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/dashboard';

  // Confirm the tenant's real name + logo from the API. On a 404 (unknown
  // subdomain) drop the optimistic branding and fall back to the manual field.
  useEffect(() => {
    if (!initialSub) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${tenantConfig.apiUrl}/api/v2/entity-config/public-branding?entity=${encodeURIComponent(initialSub)}`
        );
        if (cancelled) return;
        if (!res.ok) {
          // Unknown subdomain → drop optimistic branding, restore the manual
          // field (and reset the entity so it isn't pre-filled with the bad code).
          setBrandingEntity(null);
          setBrandingName(null);
          setEntity(import.meta.env.VITE_TENANT_CODE ?? '');
          return;
        }
        const data = await res.json();
        if (cancelled) return;
        if (data.displayName) setBrandingName(data.displayName);
        setBrandingHideTitle(data.hideTitle === true);
        if (data.hasLogo) {
          setBrandingLogoUrl(
            `${tenantConfig.apiUrl}/api/v2/entity-config/public-logo?entity=${encodeURIComponent(initialSub)}`
          );
        }
      } catch {
        // network error → keep the optimistic subdomain branding
      } finally {
        if (!cancelled) setBrandingReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [initialSub]);

  // Keep the browser tab title in sync with the tenant (optimistic + confirmed).
  useEffect(() => {
    if (brandingName) document.title = brandingName;
  }, [brandingName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const result = await login({ entity, userName, password });

      if (result.mfaRequired) {
        if (!result.isMfaSetup) {
          setIsLoadingQr(true);
          try {
            const qrResult = await getMfaQrCode();
            if (qrResult.qrCode) {
              setQrCode(qrResult.qrCode);
            }
          } catch {
            // QR code fetch failed, user will need to enter code manually
          } finally {
            setIsLoadingQr(false);
          }
        }
      } else {
        navigate(from, { replace: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('login.loginFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMfaSubmit = async () => {
    if (otpCode.length !== 6) return;

    setMfaError(null);
    try {
      await validateMfa(otpCode);
      navigate(from, { replace: true });
    } catch (err) {
      setMfaError(err instanceof Error ? err.message : t('mfa.invalidCode'));
      setOtpCode('');
    }
  };

  const handleCloseMfaDialog = () => {
    // Reset MFA state when dialog is closed
    setOtpCode('');
    setMfaError(null);
    setQrCode(null);
  };

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <img
              // Key on the src so React remounts a fresh <img> when the branding
              // logo URL arrives — otherwise a display:none set by onError on the
              // initial (missing) static fallback sticks and hides the real logo.
              key={brandingLogoUrl || 'fallback'}
              src={
                brandingLogoUrl ||
                `/assets/logos/${(brandingEntity ?? import.meta.env.VITE_TENANT_CODE)?.toLowerCase() || 'default'}/logo.png`
              }
              alt="Logo"
              className="h-12 w-12 object-contain"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          </div>
          {brandingReady && !brandingHideTitle && (
            <CardTitle className="text-2xl">{brandingName || import.meta.env.VITE_APP_NAME || 'ActiveID'}</CardTitle>
          )}
          <CardDescription>{t('login.subtitle')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </div>
            )}
            {!brandingEntity && (
              <div className="space-y-2">
                <label htmlFor="entity" className="text-sm font-medium">
                  {t('login.organizationLabel')}
                </label>
                <Input
                  id="entity"
                  type="text"
                  placeholder={t('login.organizationPlaceholder')}
                  value={entity}
                  onChange={(e) => setEntity(e.target.value.toUpperCase())}
                  required
                  autoComplete="organization"
                />
              </div>
            )}
            <div className="space-y-2">
              <label htmlFor="userName" className="text-sm font-medium">
                {t('login.usernameLabel')}
              </label>
              <Input
                id="userName"
                type="text"
                placeholder={t('login.usernamePlaceholder')}
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                required
                autoComplete="username"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium">
                {t('login.passwordLabel')}
              </label>
              <Input
                id="password"
                type="password"
                placeholder={t('login.passwordPlaceholder')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </div>
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? <LoadingSpinner size="sm" className="p-0" /> : t('login.submitButton')}
            </Button>
            <div className="text-center">
              <a
                href="/forgot-password"
                className="text-sm text-muted-foreground hover:text-primary"
              >
                {t('login.forgotPassword')}
              </a>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* MFA Dialog */}
      <Dialog open={mfaState.required} onOpenChange={(open) => !open && handleCloseMfaDialog()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <ShieldCheck className="h-6 w-6 text-primary" />
            </div>
            <DialogTitle className="text-center">{t('mfa.title')}</DialogTitle>
            <DialogDescription className="text-center">
              {!mfaState.isSetup
                ? t('mfa.setupDescription')
                : t('mfa.verifyDescription')}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col items-center space-y-6 py-4">
            {/* QR Code for first-time setup */}
            {!mfaState.isSetup && (
              <div className="flex flex-col items-center space-y-3">
                {isLoadingQr ? (
                  <div className="flex h-48 w-48 items-center justify-center rounded-lg border bg-muted">
                    <LoadingSpinner />
                  </div>
                ) : qrCode ? (
                  <div className="rounded-lg border bg-white p-2">
                    <img
                      src={`data:image/png;base64,${qrCode}`}
                      alt="MFA QR Code"
                      className="h-48 w-48"
                    />
                  </div>
                ) : (
                  <div className="flex h-48 w-48 flex-col items-center justify-center rounded-lg border bg-muted p-4 text-center">
                    <Smartphone className="mb-2 h-8 w-8 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      {t('mfa.enterCodeManually')}
                    </p>
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  {t('mfa.useAuthenticatorApp')}
                </p>
              </div>
            )}

            {/* OTP Input */}
            <div className="flex flex-col items-center space-y-3">
              <InputOTP
                maxLength={6}
                value={otpCode}
                onChange={setOtpCode}
                onComplete={handleMfaSubmit}
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>

              {mfaError && (
                <p className="text-sm text-destructive">{mfaError}</p>
              )}
            </div>

            <Button
              onClick={handleMfaSubmit}
              disabled={otpCode.length !== 6 || isLoading}
              className="w-full"
            >
              {isLoading ? <LoadingSpinner size="sm" className="p-0" /> : t('mfa.verifyButton')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
